import { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, StatusBadge } from "@/components/StatusBadge";

export default function AdminHealth() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/platform/vendor-health").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const mrr = rows.reduce((s, r) => s + (r.mrr || 0), 0);
  const expiring = rows.filter((r) => r.plan_status === "trialing" && r.trial_days_left != null && r.trial_days_left <= 7)
    .sort((a, b) => a.trial_days_left - b.trial_days_left);

  return (
    <div data-testid="admin-health-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Vendor Health</h1>
          <p className="mt-1 text-sm text-muted-foreground">Revenue, activity and churn signals across every business on the platform.</p>
        </div>
        <div className="border border-border bg-white px-5 py-3 text-right" data-testid="mrr-card">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> MRR</p>
          <p className="font-heading text-2xl font-black text-brand tabular-nums" data-testid="mrr-value">{fmt(mrr)}/mo</p>
        </div>
      </div>

      {expiring.length > 0 && (
        <div className="border border-amber-300 bg-amber-50" data-testid="trials-expiring-widget">
          <p className="border-b border-amber-200 px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-amber-800">
            Trials expiring soon ({expiring.length})
          </p>
          {expiring.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 px-5 py-3 text-sm last:border-0" data-testid={`trial-expiring-${r.id}`}>
              <div>
                <p className="font-semibold text-amber-950">{r.name}</p>
                <p className="text-xs text-amber-800">{r.contact_email}</p>
              </div>
              <span className="border border-amber-400 bg-white px-2.5 py-1 text-xs font-bold text-amber-900" data-testid={`trial-days-${r.id}`}>
                {r.trial_days_left <= 0 ? "Ends today" : `${r.trial_days_left} day${r.trial_days_left === 1 ? "" : "s"} left`}
              </span>
            </div>
          ))}
          <p className="px-5 py-2.5 text-xs text-amber-800">Nudge emails go out automatically on day 10 and day 13 — a quick call converts best.</p>
        </div>
      )}

      <div className="border border-border bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              {["Business", "Plan", "Status", "MRR", "Users", "Enquiries", "Bookings", "Orders"].map((h) => (
                <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid={`health-row-${r.id}`}>
                <td className="px-5 py-3"><p className="font-medium">{r.name}</p><p className="text-xs text-muted-foreground">/hub/{r.slug}</p></td>
                <td className="px-5 py-3 text-xs font-semibold">{r.plan}{r.fees_waived ? " (waived)" : ""}</td>
                <td className="px-5 py-3">
                  <div className="flex flex-col gap-1">
                    <StatusBadge status={r.active ? "live" : "offline"} />
                    {r.plan_status === "past_due" && <span className="border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800" data-testid={`past-due-${r.id}`}>Past due</span>}
                  </div>
                </td>
                <td className="px-5 py-3 font-semibold tabular-nums">{r.mrr ? fmt(r.mrr) : "—"}</td>
                <td className="px-5 py-3 tabular-nums">{r.users}</td>
                <td className="px-5 py-3 tabular-nums">{r.enquiries}</td>
                <td className="px-5 py-3 tabular-nums">{r.bookings}</td>
                <td className="px-5 py-3 tabular-nums">{r.orders}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">No vendors yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
